// src/Components/Documents/SharedDocuments.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  LinearProgress,
  Alert,
  Chip,
  Snackbar,
  Avatar,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useMediaQuery,
  useTheme,
  TextField,
  InputAdornment,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import {
  Download as DownloadIcon,
  InsertDriveFile as FileIcon,
  MoreVert as MoreVertIcon,
  Share as ShareIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import { documentService } from '../../services/documentService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ImageLightbox from './ImageLightbox';
import logger from '../../utils/logger';
import { downloadFile } from '../../utils/downloadUtils';

const extractStoragePathFromUrl = (url) => {
  try {
    const m = url && url.match(/\/o\/([^?]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  } catch {
    return null;
  }
};

/**
 * Helper: Get user-friendly file type label
 */
const getFileTypeLabel = (doc) => {
  // Try to extract extension from filename first
  const fileName = doc.fileName || doc.title || doc.name || '';
  const extension = fileName.split('.').pop()?.toLowerCase();

  if (extension && extension !== fileName.toLowerCase()) {
    return extension.toUpperCase();
  }

  // Fallback: Map MIME types to friendly names
  const mimeType = doc.fileType || doc.mimeType || '';
  const mimeMap = {
    'application/pdf': 'PDF',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
    'application/msword': 'Word',
    'application/vnd.ms-excel': 'Excel',
    'application/vnd.ms-powerpoint': 'PowerPoint',
    'text/plain': 'Text',
    'image/jpeg': 'JPEG',
    'image/png': 'PNG',
    'image/gif': 'GIF',
    'image/svg+xml': 'SVG'
  };

  if (mimeMap[mimeType]) {
    return mimeMap[mimeType];
  }

  // Final fallback: extract after slash
  const shortType = mimeType.split('/')[1];
  return shortType && shortType.length < 10 ? shortType.toUpperCase() : 'File';
};

/**
 * SharedDocuments Component
 *
 * Displays documents that have been shared with the current user
 * Features:
 * - View documents shared from vault
 * - Download shared documents
 * - See who shared each document
 * - View permission level (read/write)
 * - Customizable for Photos (via fileTypeFilter prop)
 *
 * Uses documentService.js for all operations
 */
const SharedDocuments = ({
  fileTypeFilter = null,           // Filter by MIME type (e.g., 'image/*' for photos)
  defaultView = 'list',             // Default view mode (for future use)
  title = 'Shared With Me',         // Page title
  emptyMessage = 'No shared documents yet. Family members can share documents with you from the vault.'
} = {}) => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // State management
  const [sharedDocuments, setSharedDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Store owner info for each document
  const [documentOwners, setDocumentOwners] = useState({});

  // Search and sort state
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date'); // 'name', 'date', 'size', 'type'
  const [sortOrder, setSortOrder] = useState('desc'); // 'asc', 'desc'

  // Image lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  /**
   * Load shared documents
   */
  const loadSharedDocuments = useCallback(async () => {
    if (!currentUser || !profile?.familyCode) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      let docs = await documentService.listSharedDocuments(
        profile.familyCode,
        currentUser.uid
      );

      // Apply fileTypeFilter if provided (e.g., 'image/*' for photos)
      if (fileTypeFilter) {
        docs = docs.filter(doc => {
          const mimeType = doc.fileType || doc.mimeType || '';

          // Handle wildcard filters (e.g., 'image/*')
          if (fileTypeFilter.includes('/*')) {
            const filterPrefix = fileTypeFilter.split('/*')[0];
            return mimeType.startsWith(filterPrefix + '/');
          }

          // Exact MIME type match
          return mimeType === fileTypeFilter;
        });
      }

      setSharedDocuments(docs);

      // Load owner information for each document
      const ownerInfo = {};
      for (const document of docs) {
        if (document.ownerId && !ownerInfo[document.ownerId]) {
          try {
            const ownerDoc = await getDoc(
              doc(db, 'families', profile.familyCode, 'members', document.ownerId)
            );
            if (ownerDoc.exists()) {
              ownerInfo[document.ownerId] = ownerDoc.data();
            }
          } catch (err) {
            logger.error('Error loading owner info:', err);
          }
        }
      }
      setDocumentOwners(ownerInfo);
    } catch (err) {
      logger.error('Error loading shared documents:', err);
      setError(err.message || 'Failed to load shared documents');
    } finally {
      setLoading(false);
    }
  }, [currentUser, profile, fileTypeFilter]);

  // Load shared documents on mount
  useEffect(() => {
    loadSharedDocuments();
  }, [loadSharedDocuments]);

  /**
   * Handle file download
   */
  const handleDownload = async (document) => {
    const filename = document.fileName || document.title || document.name || 'download';
    const url = document.fileUrl || document.downloadURL;
    const storagePath = document.storagePath || extractStoragePathFromUrl(url);
    setSnackbar({ open: true, message: `Downloading ${filename}…`, severity: 'info' });
    try {
      await downloadFile(url, filename, storagePath);
      setSnackbar({ open: true, message: `"${filename}" downloaded`, severity: 'success' });
    } catch (err) {
      logger.error('Error downloading file:', err);
      setSnackbar({ open: true, message: err.message || 'Failed to download file', severity: 'error' });
    }
  };

  /**
   * Handle menu open
   */
  const handleMenuOpen = (event, item) => {
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  /**
   * Handle menu close
   */
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  /**
   * Apply search filter to documents
   */
  const applySearch = (docs) => {
    if (!searchQuery.trim()) return docs;

    const query = searchQuery.toLowerCase();
    return docs.filter(doc => {
      const name = (doc.title || doc.fileName || doc.name || '').toLowerCase();
      const type = doc.type || '';
      return name.includes(query) || type.includes(query);
    });
  };

  /**
   * Apply sorting to documents
   */
  const applySorting = (docs) => {
    const sorted = [...docs].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case 'name':
          const nameA = (a.title || a.fileName || a.name || '').toLowerCase();
          const nameB = (b.title || b.fileName || b.name || '').toLowerCase();
          comparison = nameA.localeCompare(nameB);
          break;

        case 'date':
          const dateA = (a.updatedAt || a.createdAt)?.toMillis?.() || 0;
          const dateB = (b.updatedAt || b.createdAt)?.toMillis?.() || 0;
          comparison = dateA - dateB;
          break;

        case 'size':
          const sizeA = a.fileSize || a.size || 0;
          const sizeB = b.fileSize || b.size || 0;
          comparison = sizeA - sizeB;
          break;

        case 'type':
          const typeA = getFileTypeLabel(a);
          const typeB = getFileTypeLabel(b);
          comparison = typeA.localeCompare(typeB);
          break;

        default:
          comparison = 0;
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return sorted;
  };

  /**
   * Get processed documents (searched, sorted)
   */
  const getProcessedDocuments = () => {
    let docs = applySearch(sharedDocuments);
    docs = applySorting(docs);
    return docs;
  };

  /**
   * Toggle sort order
   */
  const toggleSortOrder = () => {
    setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  /**
   * Open image in lightbox
   */
  const handleImageClick = (clickedDoc) => {
    // Get all images from processed documents
    const images = getProcessedDocuments().filter(doc => {
      const mimeType = doc.fileType || doc.mimeType || '';
      return mimeType.startsWith('image/');
    });

    // Find index of clicked image
    const index = images.findIndex(doc => doc.id === clickedDoc.id);

    if (index !== -1) {
      setLightboxImages(images);
      setLightboxIndex(index);
      setLightboxOpen(true);
    }
  };

  /**
   * Check if document is an image
   */
  const isImage = (doc) => {
    const mimeType = doc.fileType || doc.mimeType || '';
    return mimeType.startsWith('image/');
  };

  /**
   * Get permission label for a document
   */
  const getPermissionLabel = (document) => {
    if (!document.permissions) return 'View Only';

    // Check if current user has edit permission
    if (document.permissions.canEdit && document.permissions.canEdit.includes(currentUser.uid)) {
      return 'Can Edit';
    }

    // Check if current user has share permission
    if (document.permissions.canShare && document.permissions.canShare.includes(currentUser.uid)) {
      return 'Can Share';
    }

    // Default to view only
    return 'View Only';
  };

  /**
   * Get permission color
   */
  const getPermissionColor = (document) => {
    const label = getPermissionLabel(document);
    if (label === 'Can Edit' || label === 'Can Share') return 'success';
    return 'info';
  };

  /**
   * Get permission icon
   */
  const getPermissionIcon = (document) => {
    const label = getPermissionLabel(document);
    if (label === 'Can Edit' || label === 'Can Share') return <EditIcon fontSize="small" />;
    return <VisibilityIcon fontSize="small" />;
  };

  /**
   * Format file size
   */
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  /**
   * Format date
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  /**
   * Get owner display name
   */
  const getOwnerDisplayName = (ownerId) => {
    const owner = documentOwners[ownerId];
    if (!owner) return 'Unknown';
    return `${owner.firstName || ''} ${owner.lastName || ''}`.trim() || owner.email || 'Unknown';
  };

  /**
   * Get owner initials for avatar
   */
  const getOwnerInitials = (ownerId) => {
    const owner = documentOwners[ownerId];
    if (!owner) return '?';
    const first = owner.firstName?.[0] || '';
    const last = owner.lastName?.[0] || '';
    return (first + last).toUpperCase() || owner.email?.[0]?.toUpperCase() || '?';
  };

  if (!currentUser) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please sign in to view shared documents.</Alert>
      </Box>
    );
  }

  if (!profile?.familyCode) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Please join a family to view shared documents.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <ShareIcon color="primary" />
          <Typography variant="h5">
            {title}
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          Documents that family members have shared with you
        </Typography>
      </Box>

      {/* Search and Sort Controls */}
      <Box sx={{
        display: 'flex',
        gap: 2,
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between'
      }}>
        {/* Search Field */}
        <TextField
          size="small"
          placeholder="Search shared files..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{
            minWidth: isMobile ? '100%' : 250,
            maxWidth: isMobile ? '100%' : 400
          }}
        />

        {/* Sort Controls */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Sort Dropdown */}
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel id="shared-sort-by-label">Sort by</InputLabel>
            <Select
              labelId="shared-sort-by-label"
              value={sortBy}
              label="Sort by"
              onChange={(e) => setSortBy(e.target.value)}
            >
              <MenuItem value="date">Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="size">Size</MenuItem>
              <MenuItem value="type">Type</MenuItem>
            </Select>
          </FormControl>

          {/* Sort Order Toggle */}
          <IconButton
            size="small"
            onClick={toggleSortOrder}
            title={sortOrder === 'asc' ? 'Ascending' : 'Descending'}
          >
            {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Error display */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading state */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <LinearProgress sx={{ width: '100%' }} />
        </Box>
      )}

      {/* Empty state */}
      {!loading && sharedDocuments.length === 0 && (
        <Alert severity="info" icon={<ShareIcon />}>
          {emptyMessage}
        </Alert>
      )}

      {/* Documents table */}
      {!loading && sharedDocuments.length > 0 && (
        <TableContainer component={Paper} sx={{
          mt: 2,
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: isMobile ? 500 : 650
          }
        }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align="right">Size</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Shared By</TableCell>
                <TableCell>Permission</TableCell>
                <TableCell>Date Modified</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {getProcessedDocuments().map((doc) => (
                <TableRow
                  key={doc.id}
                  hover
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <TableCell
                    onClick={() => {
                      if (isImage(doc)) {
                        handleImageClick(doc);
                      } else {
                        handleDownload(doc);
                      }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <FileIcon color="action" fontSize="small" />
                      <Typography variant="body2" noWrap sx={{ maxWidth: { xs: 140, sm: 250 } }}>
                        {doc.title || doc.fileName || doc.name || 'Untitled'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatFileSize(doc.fileSize || doc.size || 0)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {getFileTypeLabel(doc)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                        {getOwnerInitials(doc.ownerId || doc.sharedBy)}
                      </Avatar>
                      <Typography variant="body2" color="text.secondary">
                        {getOwnerDisplayName(doc.ownerId || doc.sharedBy)}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={getPermissionIcon(doc)}
                      label={getPermissionLabel(doc)}
                      size="small"
                      color={getPermissionColor(doc)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(doc.updatedAt || doc.createdAt)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMenuOpen(e, doc);
                      }}
                    >
                      <MoreVertIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={() => {
          handleDownload(menuItem);
          handleMenuClose();
        }}>
          <DownloadIcon sx={{ mr: 1 }} fontSize="small" />
          Download
        </MenuItem>
      </Menu>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Image Lightbox */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onNavigate={setLightboxIndex}
      />
    </Box>
  );
};

export default SharedDocuments;
